import { FormGroup, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js';
import countryData from '@assets/data/countries.json';
import { Country } from 'app/@types/shared/world-cities';

export class CustomValidators {
    static mustNotMatch(controlName: string, matchingControlName: string): ValidatorFn {
        return (abstractControl: AbstractControl): ValidationErrors | null => {
            const formGroup = abstractControl as FormGroup;
            const control = formGroup.controls[controlName];
            const matchingControl = formGroup.controls[matchingControlName];

            // Check if the controls are present to avoid null/undefined values
            if (!control || !matchingControl) {
                return null;
            }

            if (control.value === matchingControl.value) {
                return { mustNotMatch: true };
            }
            return null;
        };
    }

    static mustMatch(controlName: string, matchingControlName: string): ValidatorFn {
        return (abstractControl: AbstractControl): ValidationErrors | null => {
            const formGroup = abstractControl as FormGroup;
            const control = formGroup.controls[controlName];
            const matchingControl = formGroup.controls[matchingControlName];

            // Check if the controls are present to avoid null/undefined values
            if (!control || !matchingControl) {
                return null;
            }

            if (control.value !== matchingControl.value) {
                // Only set errors if matchingControl does not already have an error
                if (!matchingControl.errors?.mustMatch) {
                    matchingControl.setErrors({ mustMatch: true });
                }
                return { mustMatch: true };
            } else {
                // Clear the error if it exists and if there are no other errors
                if (matchingControl.errors && matchingControl.errors.mustMatch) {
                    delete matchingControl.errors['mustMatch'];
                    if (Object.keys(matchingControl.errors).length === 0) {
                        matchingControl.setErrors(null);
                    }
                }
            }
            return null;
        };
    }

    static passwordValidator(control: AbstractControl): ValidationErrors | null {
        const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/;
        if (regex.exec(control.value as string) !== null) {
            return null;
        } else {
            return {
                invalidPassword: true,
                errorMsg:
                    'Invalid password. The password must be at least 8 characters long, include at least one uppercase letter (A-Z), one number (0-9), and one special character (such as @, #, $, etc.).',
            };
        }
    }

    static vatValidator(control: AbstractControl): ValidationErrors | null {
        const regex =
            /^(ATU[0-9]{8}|BE[0-1][0-9]{9}|BG[0-9]{9,10}|CY[0-9]{8}[A-Z]{1}|CZ[0-9]{8,10}|DE[0-9]{9}|DK[0-9]{8}|EE[0-9]{9}|(EL|GR)[0-9]{9}|ES([A-Z][0-9]{7}[0-9A-Z]|[0-9][0-9]{7}[A-Z])|FI[0-9]{8}|FR[0-9A-Z]{2}[0-9]{9}|GB([0-9]{9}([0-9]{3})?|[A-Z]{2}[0-9]{3})|HU[0-9]{8}|IE([0-9][A-Z0-9+*][0-9]{5}[A-Z]|[0-9]{7}[A-Z]{2})|IT[0-9]{11}|LT([0-9]{9}|[0-9]{12})|LU[0-9]{8}|LV[0-9]{11}|MT[0-9]{8}|NL([0-9]{9}B[0-9]{2}|[A-Z0-9+*]{12})|PL[0-9]{10}|PT[0-9]{9}|RO[0-9]{2,10}|SE[0-9]{12}|SI[0-9]{8}|SK[0-9]{10})$/;
        if (regex.exec(control.value as string) !== null) {
            return null;
        } else {
            return {
                invalidVat: true,
                errorMsg: 'The VAT number is invalid',
            };
        }
    }

    static optionalVatValidator(control: AbstractControl): ValidationErrors | null {
        const regex =
            /^(ATU[0-9]{8}|BE[0-1][0-9]{9}|BG[0-9]{9,10}|CY[0-9]{8}[A-Z]{1}|CZ[0-9]{8,10}|DE[0-9]{9}|DK[0-9]{8}|EE[0-9]{9}|(EL|GR)[0-9]{9}|ES([A-Z][0-9]{7}[0-9A-Z]|[0-9][0-9]{7}[A-Z])|FI[0-9]{8}|FR[0-9A-Z]{2}[0-9]{9}|GB([0-9]{9}([0-9]{3})?|[A-Z]{2}[0-9]{3})|HU[0-9]{8}|IE([0-9][A-Z0-9+*][0-9]{5}[A-Z]|[0-9]{7}[A-Z]{2})|IT[0-9]{11}|LT([0-9]{9}|[0-9]{12})|LU[0-9]{8}|LV[0-9]{11}|MT[0-9]{8}|NL([0-9]{9}B[0-9]{2}|[A-Z0-9+*]{12})|PL[0-9]{10}|PT[0-9]{9}|RO[0-9]{2,10}|SE[0-9]{12}|SI[0-9]{8}|SK[0-9]{10})$/;

        if (
            control === null ||
            control === undefined ||
            regex.exec(control.value as string) !== null ||
            control.value === null ||
            control.value === '' ||
            control.value === undefined
        ) {
            return null;
        } else {
            return {
                invalidVat: true,
                errorMsg: 'The VAT number is invalid',
            };
        }
    }

    static emailValidator(control: AbstractControl): ValidationErrors | null {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (regex.exec(control.value as string) !== null) {
            return null;
        } else {
            return {
                invalidEmail: true,
                errorMsg: 'Invalid Email Format',
            };
        }
    }

    static phoneNumberValidator(control: AbstractControl): ValidationErrors | null {
        if (control.value === undefined || control.value === null || control.value === '') {
            return {
                required: true,
            };
        }
        if (control.value === '+3200000000000') {
            return {
                invalidPn: true,
                errorMsg: 'Invalid Phone Number Format',
            };
        }

        if (isValidPhoneNumber(control.value as string)) {
            return null;
        } else {
            return {
                invalidPn: true,
                errorMsg: 'Invalid Phone Number Format',
            };
        }
    }

    /**
     * Optional counterpart to {@link phoneNumberValidator}: an empty value is accepted (the phone is
     * not mandatory here — callers substitute the no-phone sentinel on submit), while a value the
     * user did type must be a valid international number. Kept separate so the required-phone forms
     * that rely on phoneNumberValidator are unaffected.
     */
    static optionalPhoneNumberValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value as string | null | undefined;
        if (value === null || value === undefined || value === '') {
            return null;
        }
        if (isValidPhoneNumber(value)) {
            return null;
        }
        return {
            invalidPn: true,
            errorMsg: 'Invalid Phone Number Format',
        };
    }

    /**
     * Normalises a user-entered phone number to E.164 (leading +, country code, digits only) for
     * submission. Cognito's AdminCreateUser / UpdateUser reject anything that is not E.164, and the
     * backend validator enforces the same format, so callers must send the normalised form rather
     * than the raw input (which may still carry the spaces or separators it was typed with). An
     * empty value maps to the no-phone sentinel that downstream code recognises to skip SMS
     * delivery; a value that cannot be parsed is returned unchanged and rejected server-side.
     */
    static toE164Phone(value: string | null | undefined): string {
        const NO_PHONE_SENTINEL = '+3200000000000';
        if (!value) {
            return NO_PHONE_SENTINEL;
        }
        try {
            return parsePhoneNumber(value).format('E.164');
        } catch {
            return value;
        }
    }

    /**
     * VAT validator driven by the *input*, not by the country dropdown: it validates whatever the
     * user types against the format of the country currently selected in the sibling `country`
     * control. This is why it lives on the control permanently (rather than being attached on
     * country change) — a new VAT is checked as soon as it is typed, and re-checked when the
     * country changes (callers just trigger `updateValueAndValidity`).
     *
     * Rules:
     * - empty is valid (VAT is optional);
     * - a value the user has never interacted with (pristine AND untouched — e.g. a legacy number
     *   loaded for editing) is not flagged, so it can't block saving unrelated fields; it starts
     *   validating once the field is edited or simply focused-then-left (blur — see onVatBlur);
     * - the per-country pattern from countries.json is anchored (`^(?:…)$`) so trailing/leading
     *   junk is rejected — keeping the verdict identical to the combined-EU pattern;
     * - a country without a known VAT format accepts all values.
     */
    static countryVatValidator(control: AbstractControl): ValidationErrors | null {
        const value = control.value as string | null;
        if (!value || (control.pristine && control.untouched)) {
            return null;
        }

        const countryCode = control.parent?.get('country')?.value as string | null | undefined;
        const country = countryData.find((c: Country) => c.id === countryCode);
        if (!country?.regexVaT) {
            return null;
        }

        const anchored = new RegExp(`^(?:${country.regexVaT})$`);
        return anchored.test(value) ? null : { invalidVat: true };
    }
}
